import { BUSINESS } from "../data/business";
import { MENU, type RawCategory, type RawGroup, type RawItem } from "../data/menu";
import type { Language } from "../i18n/strings";
import type {
  BusinessInfo,
  MenuCategory,
  MenuItem,
  ModifierGroup,
  Reservation,
  ReservationPayload,
  ReservationView,
} from "./types";

/**
 * The data layer.
 *
 * Shaped like the truck's API client so a real backend can slot in behind the
 * same calls. The menu is local data; reservations go to the Express
 * server in /server, which keeps them in Postgres (or a JSON file locally).
 */

export class ApiError extends Error {
  status: number;
  fields: string[];
  constructor(message: string, status = 0, fields: string[] = []) {
    super(message);
    this.status = status;
    this.fields = fields;
  }
}

const TOKEN_KEY = "dh_staff_token";

export function getStaffToken(): string {
  try {
    return sessionStorage.getItem(TOKEN_KEY) ?? "";
  } catch {
    return "";
  }
}

export function setStaffToken(token: string): void {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // Session storage blocked: the dashboard just asks again next time.
  }
}

async function request<T>(method: string, url: string, body?: unknown, staff = false): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (staff) headers.Authorization = `Bearer ${getStaffToken()}`;
  let response: Response;
  try {
    response = await fetch(url, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError("network", 0);
  }
  if (response.status === 204) return undefined as T;
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(data.error ?? response.statusText, response.status, data.fields ?? []);
  return data as T;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
function resolveGroup(group: RawGroup, language: Language): ModifierGroup {
  return {
    ...group,
    name: group.name[language],
    options: group.options.map((option) => ({ ...option, name: option.name[language] })),
  };
}

function resolveItem(item: RawItem, language: Language): MenuItem {
  return {
    id: item.id,
    slug: item.slug,
    name: item.name[language],
    description: item.description[language],
    price: item.price,
    image: item.image,
    diet: item.diet,
    is_seasonal: item.is_seasonal ?? false,
    is_favourite: item.is_favourite ?? false,
    allergens: item.allergens[language],
    max_per_order: item.max_per_order ?? 20,
    is_available: item.is_available ?? true,
    modifier_groups: (item.modifier_groups ?? []).map((group) => resolveGroup(group, language)),
  };
}

function resolveCategory(category: RawCategory, language: Language): MenuCategory {
  return {
    id: category.id,
    slug: category.slug,
    name: category.name[language],
    description: category.description[language],
    daypart: category.daypart,
    items: category.items.map((item) => resolveItem(item, language)),
  };
}

export const api = {
  /** Hours, address, phone from the server; the bundled copy if it is away. */
  async business(): Promise<BusinessInfo> {
    try {
      const doc = await request<BusinessInfo | null>("GET", "/api/business");
      if (doc?.hours) return doc;
    } catch {
      // Fall through to the bundled copy.
    }
    return BUSINESS;
  },

  /** The live menu from the server; the bundled one if the server is away. */
  async menuRaw(): Promise<RawCategory[]> {
    try {
      const doc = await request<{ categories: RawCategory[] } | null>("GET", "/api/menu");
      if (doc?.categories?.length) return doc.categories;
    } catch {
      // Fall through to the bundled copy.
    }
    await delay(120);
    return MENU;
  },

  async menu(language: Language): Promise<MenuCategory[]> {
    const raw = await api.menuRaw();
    return raw.map((category) => resolveCategory(category, language));
  },

  async reserve(payload: ReservationPayload): Promise<ReservationView> {
    return request<ReservationView>("POST", "/api/reservations", payload);
  },

  /** Staff-only calls behind the shared dashboard password. */
  staff: {
    async login(username: string, password: string): Promise<boolean> {
      try {
        const { token } = await request<{ ok: boolean; token: string }>("POST", "/api/auth", { username, password });
        setStaffToken(token);
        return true;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return false;
        throw error;
      }
    },
    logout(): void {
      setStaffToken("");
    },
    reservations(from: string, to: string): Promise<Reservation[]> {
      return request<Reservation[]>("GET", `/api/reservations?from=${from}&to=${to}`, undefined, true);
    },
    create(payload: ReservationPayload): Promise<Reservation> {
      return request<Reservation>("POST", "/api/reservations", payload, true);
    },
    update(id: number, patch: Partial<Pick<Reservation, "status" | "notes" | "guests" | "time" | "date">>): Promise<Reservation> {
      return request<Reservation>("PATCH", `/api/reservations/${id}`, patch, true);
    },
    remove(id: number): Promise<void> {
      return request<void>("DELETE", `/api/reservations/${id}`, undefined, true);
    },
    async upload(file: File): Promise<{ url: string; public_id: string; width: number; height: number }> {
      const body = new FormData();
      body.append("file", file);
      let response: Response;
      try {
        response = await fetch("/api/uploads", { method: "POST", headers: { Authorization: `Bearer ${getStaffToken()}` }, body });
      } catch {
        throw new ApiError("network", 0);
      }
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new ApiError(data.error ?? response.statusText, response.status);
      return data;
    },
    async menu(): Promise<RawCategory[]> {
      const doc = await request<{ categories: RawCategory[] } | null>("GET", "/api/menu");
      return doc?.categories ?? MENU;
    },
    async saveMenu(categories: RawCategory[]): Promise<RawCategory[]> {
      const doc = await request<{ categories: RawCategory[] }>("PUT", "/api/menu", { categories }, true);
      return doc.categories;
    },
    business(): Promise<BusinessInfo> {
      return request<BusinessInfo>("GET", "/api/business");
    },
    saveBusiness(info: BusinessInfo): Promise<BusinessInfo> {
      return request<BusinessInfo>("PUT", "/api/business", info, true);
    },
  },
};
